import { GameWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { FormattedGame, FormattedGameWithTeam } from '@/types/game';
import { getFilteredGames, getGamesBySeason, getSelectedGames } from '@/utils/atoms/game';
import {
  filterGamesByCompetition,
  filterGamesByGameType,
  filterGamesBySeason,
  filterGamesByTeam,
  pipeFilterFunctions,
} from '@/utils/filters';
import { convertToDropdownOption } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';
import { comparisonFiltersAtom } from '../comparison';
import {
  comparisonCompetitionIdsToFilterByAtom,
  playerComparisonCompetitionOptionsAtom,
  playerComparisonSeasonOptionsAtom,
  comparisonSeasonIdsToFilterByAtom,
} from './playerComparisonFilters';

// Raw games fetch for comparison player
const rawPlayerComparisonGamesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const comparisonId = get(comparisonIdAtom);
  const queryKey = ['playerComparisonGames', comparisonId] as const;

  const queryFn = async ({ queryKey: [, comparisonId] }: { queryKey: typeof queryKey }) => {
    if (!comparisonId) return [] as GameWithRels[];
    return (await fetch(`/player/${comparisonId}/games`)) as GameWithRels[];
  };

  return { queryKey, queryFn };
});

const playerComparisonGamesAtom = atom(async get => {
  const rawGames = await get(rawPlayerComparisonGamesAtom);
  return rawGames.data.map(objSnakeToCamel);
});

// Game filter pipeline
const playerComparisonGameFilterFunctionsAtom = atom(get => {
  const selectedGameType = get(comparisonFiltersAtom).gameType.value;
  const selectedCompetitionIds = get(comparisonCompetitionIdsToFilterByAtom);
  const selectedSeasonIds = get(comparisonSeasonIdsToFilterByAtom);
  const selectedTeamIds = get(comparisonFiltersAtom).teamIds;

  return pipeFilterFunctions<FormattedGame>(
    (entityGame: FormattedGameWithTeam[]) => filterGamesByGameType(entityGame, selectedGameType),
    (entityGame: FormattedGameWithTeam[]) => filterGamesByCompetition(entityGame, selectedCompetitionIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesBySeason(entityGame, selectedSeasonIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesByTeam(entityGame, selectedTeamIds),
  );
});

export const comparisonFilteredPlayerGamesAtom = atom(async get => {
  const { startDate, endDate, gameRange, periodRange } = get(comparisonFiltersAtom);
  return getFilteredGames(get, playerComparisonGamesAtom, playerComparisonGameFilterFunctionsAtom, {
    startDate,
    endDate,
    gameRange,
    periodRange,
  });
});

const playerComparisonGamesOptionsAtom = atom(async get => {
  const games = await get(comparisonFilteredPlayerGamesAtom);
  return games.map(({ game: { gameId, name } }) => convertToDropdownOption(name, gameId));
});

export const playerComparisonSelectedGamesAtom = atom(async get =>
  getSelectedGames(get, playerComparisonGamesOptionsAtom, get(comparisonFiltersAtom).deselectedGameIds),
);

export const playerComparisonGamesBySeasonAtom = atom(get =>
  getGamesBySeason(
    get,
    playerComparisonSeasonOptionsAtom,
    playerComparisonCompetitionOptionsAtom,
    comparisonFilteredPlayerGamesAtom,
  ),
);

const playerComparisonSelectedGamesForQueryAtom = atom(async get => {
  const selectedGames = await get(playerComparisonSelectedGamesAtom);
  const games = selectedGames.length > 0 ? selectedGames : await get(playerComparisonGamesOptionsAtom);
  return games.map(({ value }) => value);
});

export const unwrappedPlayerComparisonSelectedGamesForQueryAtom = unwrap(
  playerComparisonSelectedGamesForQueryAtom,
  prev => prev || [],
);
